import { useState, useContext, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { IconDotsVertical } from "@tabler/icons-react";

import { MyDetails } from "./tabs/MyDetails";
import { CompanyDetails } from "./tabs/CompanyDetails";
import { PricingDetails } from "./tabs/Plans";
import AppearanceSettings from "./tabs/AppearanceSettings";
import { CustomJoinPageTab } from "./tabs/CustomJoinPage";
import { useCustomJoinPageState } from "./tabs/CustomJoinPage/useCustomJoinPageState";
import { BrandContext } from "../../../contexts/BrandContext/context";
import { planService } from "../../../services/planService";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { UnderlineTabs } from "@/components/ui/underline-tabs";

const TABS = [
  { id: "my-details", label: "My details", component: MyDetails },
  {
    id: "company-details",
    label: "Company details",
    component: CompanyDetails,
  },
  {
    // id kept as "appearance-settings" so existing deep links and the
    // onboarding tour anchor keep working after the rename.
    id: "appearance-settings",
    label: "Customizations",
    component: AppearanceSettings,
  },
  {
    // Admin-enabled per brand; hidden from the tab bar until then.
    id: "custom-page",
    label: "Custom page",
    component: CustomJoinPageTab,
  },
  { id: "pricing", label: "Pricing details", component: PricingDetails },
] as const;

type TabId = (typeof TABS)[number]["id"];

export default function Settings() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("my-details");
  const { brandDetails, fetchBrandDetails } = useContext(BrandContext);

  const { data: subscription } = useQuery({
    queryKey: ["subscription"],
    queryFn: () => planService.getCurrentSubscription(),
  });

  const hasActiveSubscription = !!subscription?.data?.active;
  const { data: customJoinPage } = useCustomJoinPageState();
  const customPageEnabled = Boolean(customJoinPage?.enabled);

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && TABS.some((tab) => tab.id === tabFromUrl)) {
      setActiveTab(tabFromUrl as TabId);
    } else if (!tabFromUrl) {
      const defaultTab = hasActiveSubscription ? "my-details" : "pricing";
      setActiveTab(defaultTab);
      navigate(`/brand/settings?tab=${defaultTab}`);
    }
  }, [searchParams, hasActiveSubscription, navigate]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        if (!brandDetails) await fetchBrandDetails();
      } catch {
        toast.error("Could not load your brand details");
      }
    };
    fetchDetails();
  }, [fetchBrandDetails]);

  const ActiveComponent = TABS.find((tab) => tab.id === activeTab)?.component;

  const visibleTabs = (hasActiveSubscription ? TABS : TABS.filter((tab) => tab.id === "pricing")).filter(
    (tab) => tab.id !== "custom-page" || customPageEnabled
  );

  const handleTabChange = (tabId: TabId) => {
    if (!hasActiveSubscription && tabId !== "pricing") {
      toast.error("Subscribe to a plan to open this section");
      return;
    }
    setActiveTab(tabId);
    navigate(`/brand/settings?tab=${tabId}`);
  };

  // Settings has no verb of its own; saving lives inside each tab. These
  // are places to go, so none of them is the primary button.
  const links = [
    { label: "Partner requests", go: () => navigate("/brand/agency-requests") },
    { label: "Reveshare Amped", go: () => navigate("/brand/reveshare-amped/signup") },
    {
      label: "Rate Reveshare",
      go: () => window.open("https://apps.shopify.com/reveshare/reviews", "_blank", "noopener"),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="mb-6 flex items-center justify-between gap-2 sm:mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <div className="hidden items-center gap-2 sm:flex">
          {links.map((link) => (
            <Button key={link.label} variant="outline" onClick={link.go}>
              {link.label}
            </Button>
          ))}
        </div>
        <div className="sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More">
                <IconDotsVertical size={16} stroke={1.75} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {links.map((link) => (
                <DropdownMenuItem key={link.label} onClick={link.go}>
                  {link.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <UnderlineTabs
        tabs={visibleTabs.map((tab) => ({ value: tab.id, label: tab.label, id: tab.id }))}
        value={activeTab}
        onValueChange={(value) => handleTabChange(value as TabId)}
        accent="brand"
      />

      {ActiveComponent && <ActiveComponent />}
    </div>
  );
}
